// Undo/redo for the story editor: a stack of whole source strings. Every
// operation in storySource.ts returns a new string, so a step back is the old
// string — there is no inverse to compute and nothing to get wrong.
//
// Typing coalesces: consecutive edits carrying the same key (one element, one
// field) replace the newest entry instead of pushing, so undo removes a burst
// of typing, not one character.

export interface StoryHistory {
  past: string[]
  present: string
  future: string[]
  /** Key of the edit that produced `present`; a following edit with the same key replaces it. */
  coalesceKey: string | null
}

/** Enough to undo a long session without holding every keystroke of a big page. */
export const HISTORY_LIMIT = 200

export function initHistory(source: string): StoryHistory {
  return { past: [], present: source, future: [], coalesceKey: null }
}

export function pushEdit(h: StoryHistory, next: string, coalesceKey?: string): StoryHistory {
  if (next === h.present) return h
  if (coalesceKey && coalesceKey === h.coalesceKey && h.past.length > 0) {
    return { past: h.past, present: next, future: [], coalesceKey }
  }
  const past = [...h.past, h.present]
  if (past.length > HISTORY_LIMIT) past.splice(0, past.length - HISTORY_LIMIT)
  return { past, present: next, future: [], coalesceKey: coalesceKey ?? null }
}

export function undo(h: StoryHistory): StoryHistory {
  if (h.past.length === 0) return h
  const past = h.past.slice(0, -1)
  return { past, present: h.past[h.past.length - 1], future: [h.present, ...h.future], coalesceKey: null }
}

export function redo(h: StoryHistory): StoryHistory {
  if (h.future.length === 0) return h
  const [next, ...future] = h.future
  return { past: [...h.past, h.present], present: next, future, coalesceKey: null }
}
